import { Component } from 'react'
import { createStructuredSelector } from 'reselect'
import { connect } from 'react-redux'
import styled from 'styled-components'
import onClickOutside from 'react-onclickoutside'
import { Tooltip } from 'react-tippy'
import ReactCountryFlag from 'react-country-flag'
import { cloneDeep } from 'lodash'

import { palette, fonts, mediaQueries, shadows } from '../../styles/variables'
import { translate } from '../../utils/translation'
import Icon, { ArrowIcon, Line } from '../icon/Icon'
import websiteIcon from '../../styles/assets/website.svg'
import { getLocationInfo, removeLastPart } from '../../utils/locations'
import { selectCachedLocations } from '../../state/selectors/locationManager'
import TextOverflow from '../textoverflow/TextOverflow'

const OptionWrapper = styled.div((props) => ({
  display: 'flex',
  height: '52px',
  width: '100%',
  padding: '10px 16px',
  alignItems: 'center',
  color: palette.darkGray,
  cursor: 'pointer',
  background: props.checked ? palette.blue20 : 'transparent',
}))

const OptionListWrapper = styled.div((props) => ({
  position: 'absolute',
  [props.direction === 'top' ? 'bottom' : 'top']:
    props.direction === 'top' ? '50px' : 'calc(100% + 8px)',
  left: '0',
  width: '100%',
  maxHeight: '200px',
  background: palette.white,
  borderRadius: '2px',
  overflow: 'auto',
  boxShadow: shadows.shadowA,
}))

const OptionList = styled.div({
  maxHeight: '200px',
  overflowY: 'auto',
  '& div:hover': {
    cursor: 'pointer',
  },
})

export const LocationOption = styled.div({
  position: 'relative',
  marginLeft: '8px',
  '& > *:nth-child(1)': {
    fontSize: '14px',
    lineHeight: '16px',
  },
  '& > *:nth-child(2)': {
    display: 'block',
    marginTop: '2px',
    fontSize: '12px',
    lineHeight: '14px',
    fontFamily: fonts.primaryLight,
    color: `${palette.darkGray50} !important`,
  },
})

export const SelectedLocation = styled.div({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
})

const Option = (props) => {
  const { cachedLocations, value, checked, onCheck } = props
  const { lang, loc_id } = value
  if (loc_id === 'all') {
    return (
      <>
        <OptionWrapper
          data-testid="multi-location-selector-option"
          checked={checked}
        >
          <OptionCheckbox>
            <label className="container">
              <input type="checkbox" checked={checked} onChange={onCheck} />
              <span className="checkmark" style={{ left: 0 }} />
            </label>
          </OptionCheckbox>
          <Tooltip
            open={false}
            position="top"
            title={`${translate('all_locations')}`}
            style={{
              flex: 1,
              overflow: 'hidden',
            }}
          >
            <SelectedLocation style={{ width: 'auto' }} onClick={onCheck}>
              <Icon src={websiteIcon} size={20} />
              <LocationOption>
                <div>{`${translate('all_locations')}`}</div>
                <div>{translate('all_languages')}</div>
              </LocationOption>
            </SelectedLocation>
          </Tooltip>
        </OptionWrapper>
        <Line
          style={{ margin: '8px 0', paddingTop: 0 }}
          color={palette.lightGray}
        />
      </>
    )
  }
  const locationInfo = getLocationInfo(loc_id, cachedLocations)
  const parentLocationInfo = locationInfo.parentId
    ? getLocationInfo(locationInfo.parentId)
    : locationInfo

  return (
    <OptionWrapper checked={checked}>
      <OptionCheckbox>
        <label className="container">
          <input type="checkbox" checked={checked} onChange={onCheck} />
          <span className="checkmark" style={{ left: 0 }} />
        </label>
      </OptionCheckbox>
      <Tooltip
        open={false}
        position="top"
        title={`${translate(locationInfo.canonicalName)}`}
        style={{
          flex: 1,
          overflow: 'hidden',
        }}
      >
        <SelectedLocation style={{ width: 'auto' }} onClick={onCheck}>
          <ReactCountryFlag
            countryCode={locationInfo.countryIsoCode}
            svg
            style={{
              marginTop: '-3px',
              width: '1.3333em',
              height: '1em',
            }}
          />
          <LocationOption>
            <div>{`${translate(parentLocationInfo.canonicalName)}`}</div>
            {locationInfo.parentId ? (
              <div>
                {`${translate(lang)} - ${removeLastPart(
                  translate(locationInfo.canonicalName),
                )}`}
              </div>
            ) : (
              <div>{`${translate(lang)}`}</div>
            )}
          </LocationOption>
        </SelectedLocation>
      </Tooltip>
    </OptionWrapper>
  )
}

const SelectedValue = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0 20px',
  height: '40px',
  width: '100%',
})

const Container = styled.div((props) => ({
  position: 'relative',
  width: '100%',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  border: `1px solid ${props.borderColor || palette.lightGray}`,
  borderRadius: '2px',
  cursor: 'pointer',
  [mediaQueries.mobile]: {
    flexDirection: 'column',
    flexFlow: 'column-reverse',
  },
}))

const OptionCheckbox = styled.div((props) => ({
  display: 'flex',
  flex: '0 0 32px',
  justifyContent: 'flex-start',
  alignItems: 'center',
  borderRight: '1px solid white',
  borderLeft: props.active ? `3px solid ${palette.orange}` : 'none',
}))

class MultiLocationSelector extends Component {
  constructor(props) {
    super(props)

    this.state = {
      isOpen: false,
    }
  }

  // eslint-disable-next-line react/no-unused-class-component-methods
  handleClickOutside = () => {
    this.setState({ isOpen: false })
  }

  openList = () => {
    this.setState((prev) => ({ isOpen: !prev.isOpen }))
  }

  onCheckItem = (item) => {
    const { value, options, renderValue, onChange } = this.props
    let newValue = cloneDeep(value)

    if (item.loc_id === 'all') {
      newValue = this.isChecked(item) ? [] : cloneDeep(options)
    } else if (this.isChecked(item)) {
      newValue.splice(
        newValue.findIndex((v) => renderValue(v) === renderValue(item)),
        1,
      )
    } else {
      newValue.push(item)
    }
    onChange(newValue)
  }

  isChecked = (item) => {
    const { value, options, renderValue } = this.props
    if (item.loc_id === 'all') {
      return value.length === options.length
    }
    return value.some((v) => renderValue(v) === renderValue(item))
  }

  render() {
    const { isOpen } = this.state
    const {
      options,
      value,
      direction = 'down',
      renderValue,
      cachedLocations,
    } = this.props
    const allLocation = { loc_id: 'all', lang: 'all' }
    const error = value.length === 0
    const borderColor = isOpen || error ? palette.orange : palette.lightGray

    return (
      <Container borderColor={borderColor}>
        <SelectedValue
          data-testid="multi-location-selector-value"
          className="selector-value"
          onClick={this.openList}
        >
          <SelectedLocation style={{ width: 'calc(100% - 25px)' }}>
            <Icon src={websiteIcon} size={24} margin="0 10px 0 0" />
            <div style={{ width: 'inherit' }}>
              <TextOverflow
                display="block"
                text={
                  this.isChecked(allLocation)
                    ? translate('total_locations', options.length)
                    : translate('locations_selected', value.length)
                }
              />
            </div>
          </SelectedLocation>
          {isOpen ? (
            <ArrowIcon
              up={direction === 'down'}
              down={direction === 'top'}
              color={palette.darkGray}
              style={{ marginRight: '10px' }}
            />
          ) : (
            <ArrowIcon
              up={direction === 'top'}
              down={direction === 'down'}
              color={palette.darkGray}
              style={{ marginRight: '10px' }}
            />
          )}
        </SelectedValue>
        {isOpen && (
          <OptionListWrapper direction={direction}>
            <OptionList>
              <Option
                key={renderValue(allLocation)}
                value={allLocation}
                checked={this.isChecked(allLocation)}
                onCheck={() => this.onCheckItem(allLocation)}
              />
              {options.map((c) => (
                <Option
                  key={renderValue(c)}
                  value={c}
                  checked={this.isChecked(c)}
                  onCheck={() => this.onCheckItem(c)}
                  cachedLocations={cachedLocations}
                />
              ))}
            </OptionList>
          </OptionListWrapper>
        )}
      </Container>
    )
  }
}

const mapStateToProps = createStructuredSelector({
  cachedLocations: selectCachedLocations(),
})

const mapDispatchToProps = () => ({})

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(onClickOutside(MultiLocationSelector))
